import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Alert,
  IconButton,
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { getToken, base_url } from "../../config/config";

const AddShift = () => {
  const [name, setName] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [days, setDays] = useState([]);
  const [breaks, setBreaks] = useState([{ breakStart: "", breakEnd: "" }]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const availableDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const handleCheckboxChange = (event) => {
    const { value } = event.target;
    if (days.includes(value)) {
      setDays(days.filter((day) => day !== value)); // Remove day from the list if already selected
    } else {
      setDays([...days, value]); // Add day to the list if not selected
    }
  };

  // Handle break time change
  const handleBreakChange = (index, field, value) => {
    const newBreaks = [...breaks];
    newBreaks[index][field] = value;
    setBreaks(newBreaks);
  };

  // Add a new break time field
  const handleAddBreak = () => {
    setBreaks([...breaks, { breakStart: "", breakEnd: "" }]);
  };

  // Remove a break time field
  const handleRemoveBreak = (index) => {
    const newBreaks = breaks.filter((_, i) => i !== index);
    setBreaks(newBreaks);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const shiftData = { name, startTime, endTime, days, breaks };

      // Post the data to your backend API
      const response = await axios.post(`${base_url}/v1/addShift`, shiftData);

      if (response.status === 201) {
        setSuccess("Shift added successfully");
        setError("");
        setName(""); // Reset the name field
        setStartTime("");
        setEndTime("");
        setDays([]);
        setBreaks([{ breakStart: "", breakEnd: "" }]); // Reset breaks
      }
    } catch (error) {
      setError(
        "Error adding shift: " + error.response?.data?.message || error.message,
      );
      setSuccess("");
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 600,
        margin: "0 auto",
        padding: "2rem",
        backgroundColor: "#f5f5f5",
        borderRadius: "10px",
        boxShadow: "0 3px 10px rgba(0, 0, 0, 0.2)",
      }}>
      <Typography variant='h4' gutterBottom align='center'>
        Add Shift
      </Typography>

      {success && <Alert severity='success'>{success}</Alert>}
      {error && <Alert severity='error'>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label='Shift Name'
              variant='outlined'
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label='Start Time'
              type='time'
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label='End Time'
              type='time'
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant='subtitle1' gutterBottom>
              Select Days:
            </Typography>
            <FormGroup row>
              {availableDays.map((day) => (
                <FormControlLabel
                  key={day}
                  control={
                    <Checkbox
                      value={day}
                      checked={days.includes(day)}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label={day}
                />
              ))}
            </FormGroup>
          </Grid>

          <Grid item xs={12}>
            <Typography variant='subtitle1' gutterBottom>
              Break Times:
            </Typography>

            {breaks.map((breakTime, index) => (
              <Grid container spacing={2} key={index}>
                <Grid item xs={5}>
                  <TextField
                    label='Break Start'
                    type='time'
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={breakTime.breakStart}
                    onChange={(e) =>
                      handleBreakChange(index, "breakStart", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={5}>
                  <TextField
                    label='Break End'
                    type='time'
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={breakTime.breakEnd}
                    onChange={(e) =>
                      handleBreakChange(index, "breakEnd", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={2}>
                  <IconButton
                    aria-label='remove break'
                    onClick={() => handleRemoveBreak(index)}
                    disabled={breaks.length === 1}>
                    <RemoveCircleOutline />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Button
              variant='outlined'
              color='primary'
              startIcon={<AddCircleOutline />}
              onClick={handleAddBreak}
              sx={{ mt: 2 }}>
              Add Break
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Button type='submit' variant='contained' color='primary' fullWidth>
              Add Shift
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default AddShift;
